import { useState, useEffect } from 'react';
import adminService from '../services/adminService';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X, Loader2, Star, Crown } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  price: number;
  duration: number;
  features: string[];
  popular: boolean;
  isAgent: boolean;
  isActive: boolean;
}

const EMPTY_FORM = {
  id: '',
  name: '',
  price: '',
  duration: '30',
  features: '',
  popular: false,
  isAgent: false,
};

export function PlansManagement() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminService.getPlans();
      setPlans(res?.plans || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (plan: Plan) => {
    setForm({
      id: plan.id,
      name: plan.name,
      price: String(plan.price),
      duration: String(plan.duration),
      features: plan.features.join('\n'),
      popular: plan.popular,
      isAgent: plan.isAgent,
    });
    setEditingId(plan.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.id.trim() || !form.name.trim() || !form.price || !form.duration) {
      toast.error('ID, Name, Price, and Duration are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: form.id.trim().toLowerCase().replace(/\s+/g, '-'),
        name: form.name.trim(),
        price: parseFloat(form.price),
        duration: parseInt(form.duration),
        features: form.features.split('\n').map(f => f.trim()).filter(Boolean),
        popular: form.popular,
        isAgent: form.isAgent,
      };

      if (editingId) {
        await adminService.updatePlan(editingId, payload);
        toast.success('Plan updated');
      } else {
        await adminService.createPlan(payload);
        toast.success('Plan created');
      }
      setShowForm(false);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(`Delete plan "${id}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await adminService.deletePlan(id);
      setPlans(prev => prev.filter(p => p.id !== id));
      toast.success('Plan deleted');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete plan');
    } finally {
      setDeletingId(null);
    }
  };

  const userPlans = plans.filter(p => !p.isAgent);
  const agentPlans = plans.filter(p => p.isAgent);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Plans</h2>
          <p className="text-gray-500 text-sm mt-1">{plans.length} plans · Changes reflect instantly in the app</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white gap-2"
        >
          <Plus className="size-4" /> Add Plan
        </Button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50 pb-3">
            <h3 className="font-bold text-gray-800">{editingId ? 'Edit Plan' : 'New Plan'}</h3>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Plan ID <span className="text-red-500">*</span></Label>
                <Input value={form.id} onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                  placeholder="e.g. gold" disabled={!!editingId} className="mt-1" />
                <p className="text-xs text-gray-400 mt-0.5">Lowercase, no spaces. Cannot change after creation.</p>
              </div>
              <div>
                <Label>Plan Name <span className="text-red-500">*</span></Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Gold" className="mt-1" />
              </div>
              <div>
                <Label>Price ($) <span className="text-red-500">*</span></Label>
                <Input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                  placeholder="1999" min="0" className="mt-1" />
              </div>
              <div>
                <Label>Duration (days) <span className="text-red-500">*</span></Label>
                <Input type="number" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                  placeholder="30" min="1" className="mt-1" />
              </div>
            </div>

            <div>
              <Label>Features <span className="text-gray-400 font-normal">(one per line)</span></Label>
              <Textarea value={form.features} onChange={e => setForm(f => ({ ...f, features: e.target.value }))}
                placeholder={'Unlimited messaging\nProfile boost\nPriority support'} rows={5} className="mt-1 resize-none" />
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.popular} onChange={e => setForm(f => ({ ...f, popular: e.target.checked }))}
                  className="size-4 accent-purple-600" />
                <span className="text-sm font-medium">Mark as Popular</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isAgent} onChange={e => setForm(f => ({ ...f, isAgent: e.target.checked }))}
                  className="size-4 accent-blue-600" />
                <span className="text-sm font-medium">Agent Plan</span>
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white gap-2">
                {saving ? <><Loader2 className="size-4 animate-spin" /> Saving...</> : <><Check className="size-4" /> Save Plan</>}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="gap-2">
                <X className="size-4" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-8 animate-spin text-purple-500" />
        </div>
      ) : (
        <>
          {/* User Plans */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Crown className="size-4 text-purple-500" /> User Plans
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {userPlans.map(plan => (
                <PlanCard key={plan.id} plan={plan} onEdit={openEdit} onDelete={handleDelete} deletingId={deletingId} />
              ))}
              {userPlans.length === 0 && (
                <p className="text-gray-400 text-sm col-span-full">No user plans yet.</p>
              )}
            </div>
          </div>

          {/* Agent Plans */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Star className="size-4 text-blue-500" /> Agent Plans
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {agentPlans.map(plan => (
                <PlanCard key={plan.id} plan={plan} onEdit={openEdit} onDelete={handleDelete} deletingId={deletingId} />
              ))}
              {agentPlans.length === 0 && (
                <p className="text-gray-400 text-sm col-span-full">No agent plans yet.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PlanCard({ plan, onEdit, onDelete, deletingId }: {
  plan: Plan;
  onEdit: (p: Plan) => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
}) {
  return (
    <Card className={`relative overflow-hidden border shadow-md ${plan.popular ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-200'}`}>
      {plan.popular && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-center py-1 text-xs font-bold">
          MOST POPULAR
        </div>
      )}
      <CardContent className={`p-4 space-y-3 ${plan.popular ? 'pt-8' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="font-bold text-gray-800">{plan.name}</h4>
            <p className="text-2xl font-bold text-purple-600 mt-0.5">${plan.price.toLocaleString()}</p>
            <p className="text-xs text-gray-500">{plan.duration} days</p>
          </div>
          <div className="flex flex-col gap-1">
            {plan.isAgent && <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200">Agent</Badge>}
          </div>
        </div>

        <ul className="space-y-1">
          {plan.features.slice(0, 4).map((f, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
              <Check className="size-3 text-green-500 mt-0.5 flex-shrink-0" />
              {f}
            </li>
          ))}
          {plan.features.length > 4 && (
            <li className="text-xs text-gray-400">+{plan.features.length - 4} more</li>
          )}
        </ul>

        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={() => onEdit(plan)}
            className="flex-1 gap-1 text-xs border-purple-200 text-purple-700 hover:bg-purple-50">
            <Pencil className="size-3" /> Edit
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(plan.id)}
            disabled={deletingId === plan.id}
            className="text-xs border-red-200 text-red-600 hover:bg-red-50 px-2">
            {deletingId === plan.id ? <Loader2 className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
